import { Badge } from "@/modern/components/ui/badge";
import { ChevronDown, Loader2, Upload } from "lucide-react";
import { EntityFileProvider, useEntityFileContext, CompanyCamButton, FileMenu, FileSliderView } from "@/react/components/entityViews/files";
import { useEffect, useRef, useState, useCallback } from "react";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { useEntityManagerContext } from "../context/EntityManagerContext";
import { WidgetParamKeys } from "@/types/entity";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

export function EntityManagerFileBar() {
  const { record, entityType } = useEntityManagerContext();
  return (
    <EntityFileProvider record={record} entityType={entityType}>
      <EntityManagerFileBarContent />
    </EntityFileProvider>
  );
}

const EntityManagerFileBarContent = () => {
  const { record } = useEntityManagerContext();
  const { files, totalFilesCount, isFetchingPhotos, openedFile, setIsFileEditorOpen, isFileEditorOpen, uploadNewFile, handleFileOpen, fetchCompanyCamPhotoById } = useEntityFileContext();
  const { project } = useSwivuBryntumContext();
  const uploadNewFileRef = useRef<HTMLInputElement>(null);

  const hasFiles = files.length > 0;

  // File bar open/closed state with persistence
  const [isOpen, setIsOpenState] = useState(() => {
    const persistedState = project?.userPreferencesStore?.getSetting(PERSISTENCE_STATE_KEYS.fileBarExpandedState) === 'true' || false;
    return persistedState;
  });

  const setIsOpen = useCallback((isOpen: boolean) => {
    setIsOpenState(isOpen);
    project?.userPreferencesStore?.updateSetting(PERSISTENCE_STATE_KEYS.fileBarExpandedState, String(isOpen));
  }, [project]);

  // Auto-open only when files are first added (transition from empty to having files)
  useEffect(() => {
    if (project) {
      const persistedState = project?.userPreferencesStore?.getSetting(PERSISTENCE_STATE_KEYS.fileBarExpandedState);
      if (persistedState === undefined && hasFiles) {
        setIsOpen(true);
      }
    }
  }, [hasFiles, setIsOpen, project]);

  /* Business Logic Hooks */
  const { callParamAction } = useEntityManagerContext();

  // Register widget param action for file deep linking
  useEffect(() => {
    callParamAction(WidgetParamKeys.FILE.EDIT, (fileId) => {
      if (!fileId) return false;

      // First, try to find the file in the existing files array
      const existingFile = files.find((file) => String(file.id) === String(fileId));

      if (existingFile) {
        handleFileOpen(existingFile);
        return true;
      }

      // If not found, try to fetch it as a CompanyCam photo (async operation)
      fetchCompanyCamPhotoById(fileId)
        .then((companyCamPhoto) => {
          if (companyCamPhoto) {
            handleFileOpen(companyCamPhoto);
          }
        })
        .catch((error) => {
          console.error("Error fetching CompanyCam photo:", error);
        });

      // Return true to indicate we're handling it (even if async)
      return true;
    });
  }, [callParamAction, files, handleFileOpen, fetchCompanyCamPhotoById]);

  // Allow user control over expanded state
  const shouldShowExpanded = isOpen;

  return (
    <Collapsible open={shouldShowExpanded} onOpenChange={setIsOpen} className="border-b border-border">
      <div className="py-2 sm:py-3 px-3 sm:px-6">
        {/* Header with Add Files button */}
        <div className="flex items-center justify-between">
          {/* Always show collapsible header */}
          <CollapsibleTrigger className="flex items-center gap-2 group -ml-2 px-2 py-1 rounded-md hover:bg-muted/50 transition-colors">
            <ChevronDown className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:text-foreground",
              !shouldShowExpanded && "transform -rotate-90"
            )} />
            <h3 className="text-base text-foreground relative">
              Files
              <Badge variant="muted" size="xs" className="absolute -right-7 -top-3">
                {totalFilesCount}
              </Badge>
            </h3>
          </CollapsibleTrigger>

          <div className="flex items-center gap-4">
            <label className="hidden">
              <input
                ref={uploadNewFileRef}
                type="file"
                className="hidden"
                multiple
                onChange={async (e) => {
                  if (e.target.files) {
                    await uploadNewFile(e.target.files);
                    e.target.value = "";
                  }
                }}
                id="file-upload"
              />
            </label>

            {isFetchingPhotos && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <CompanyCamButton />
            <FileMenu />
          </div>
        </div>

        <CollapsibleContent>
          <div className="mt-4">
            {/* Empty state */}
            {files.length === 0 && !isFetchingPhotos && (
              <button
                className="space-y-2 flex flex-col"
                onClick={() => {
                  uploadNewFileRef.current?.click();
                }}
              >
                <div className="w-24 h-24 rounded-md shrink-0 border flex justify-center items-center">
                  <Upload />
                </div>
                <div className="text-xs h-4 text-muted-foreground">Add file</div>
              </button>
            )}

            {/* Files Slider */}
            {hasFiles && <FileSliderView />}
          </div>
        </CollapsibleContent>
      </div>

      {openedFile && (
        <FileDetailModal entityRecord={record} file={openedFile} open={isFileEditorOpen} setOpen={setIsFileEditorOpen} />
      )}

      {/* <FileEditor file={openedFile} open={isFileEditorOpen} setOpen={setIsFileEditorOpen} /> */}
    </Collapsible>
  );
};